import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChromaCard } from './ChromaCard.jsx';
import { EmailCopy } from './EmailCopy.jsx';

gsap.registerPlugin(ScrollTrigger);

// Pinned-scroll storytelling: the entire .about-pinned-stage element pins
// while five paragraphs cross-fade in the right column. The wrap is sized
// so each paragraph gets ~80vh of scroll budget. Lenis-friendly because
// we use scrub:true (ScrollTrigger drives the timeline, not Lenis directly).
//
// The pin only makes sense when the whole stage (heading + portrait + one
// paragraph + meter) fits inside a single viewport. Narrow viewports are
// handled by the 860px breakpoint that the CSS shares; SHORT viewports
// (landscape phones, 16:9 laptops with a lot of browser chrome, half-height
// windows) used to fall through that gate and got a pinned stage whose
// `overflow: hidden` clipped the bottom of the portrait and the meta line.
// The height floor below is the smallest stage that still fits comfortably:
// stage padding + heading + a five-line paragraph + meter ≈ 560px.
// gsap.matchMedia re-evaluates on resize / rotation, so the layout switches
// modes live instead of freezing whatever mode was picked on mount.
const PINNED_MEDIA =
  '(min-width: 861px) and (min-height: 601px) and (prefers-reduced-motion: no-preference)';

const PARAGRAPHS = [
  {
    eyebrow: '01',
    body: (
      <>
        Hi, I&rsquo;m <strong>Zongheng Brian He</strong> — a student at Xavier College Preparatory
        High School in Palm Desert, and a Los Angeles–based young visual artist working at the
        intersection of <em className="accent-red">emotion, identity, and human connection</em>. My
        work is meant to be felt, not just seen.
      </>
    ),
  },
  {
    eyebrow: '02',
    body: (
      <>
        I work across <strong>sculpture, painting, photography, and digital media</strong>, drawn
        again and again to themes of choice, control, and the quiet weight of{' '}
        <em className="accent-red">lived experience</em>. The pieces I make are minimal, symbolic,
        reflective — built to hold the subtle depth of everyday moments.
      </>
    ),
  },
  {
    eyebrow: '03',
    body: (
      <>
        I&rsquo;m the founder of <span className="mark-red">Color and Care</span>, a student-led
        nonprofit that uses art to bring warmth and support to children — especially those with
        autism. Through color, creativity, and shared experiences, the project transforms art into a
        space of <em className="accent-red">care, connection, and expression</em>.
      </>
    ),
  },
  {
    eyebrow: '04',
    body: (
      <>
        I&rsquo;m also developing <span className="mark-red">Made to Be Seen</span>, a creative
        practice rooted in the belief that every voice and form of expression deserves visibility —
        translating overlooked perspectives into design, story, and form on a meaningful platform.
      </>
    ),
  },
  {
    eyebrow: '05',
    body: (
      <span className="about-pinned-quote">
        Creating with purpose.
        <br />
        <em>Connecting through color.</em>
      </span>
    ),
  },
];

export function AboutPinned() {
  const wrapRef = useRef(null);
  const stageRef = useRef(null);
  const paragraphsRef = useRef([]);
  const progressBarRef = useRef(null);
  const counterRef = useRef(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const stage = stageRef.current;
    if (!wrap || !stage) return undefined;

    const mm = gsap.matchMedia();

    // Below the thresholds (or under reduced motion) nothing runs: the CSS
    // default is the stacked, unpinned layout, so no inline styles are needed.
    mm.add(PINNED_MEDIA, () => {
      const paragraphs = paragraphsRef.current.filter(Boolean);
      const total = PARAGRAPHS.length;

      // `.is-pinned` switches the stylesheet into pinned mode (500vh wrap,
      // 100svh stage, absolutely stacked paragraphs). Added synchronously in
      // a layout effect, so the first paint already shows the pinned layout.
      wrap.classList.add('is-pinned');

      // Initial: paragraph 0 fully visible, others hidden + offset
      paragraphs.forEach((el, idx) => {
        gsap.set(el, {
          opacity: idx === 0 ? 1 : 0,
          y: idx === 0 ? 0 : 40,
          zIndex: idx === 0 ? 2 : 1,
        });
      });

      ScrollTrigger.create({
        trigger: wrap,
        start: 'top top',
        end: () => `+=${wrap.offsetHeight - window.innerHeight}`,
        pin: stage,
        pinSpacing: true,
        scrub: 0.5,
        onUpdate: (self) => {
          const progress = self.progress;
          const step = 1 / total;
          // For each paragraph, compute its window of visibility.
          paragraphs.forEach((el, idx) => {
            const segStart = idx * step;
            const segEnd = (idx + 1) * step;
            // Crossfade window: 25% of a segment is fade-in/out
            const fadeBand = step * 0.25;
            let opacity = 0;
            let y = 40;
            let z = 1;
            if (progress >= segStart - fadeBand && progress < segEnd + fadeBand) {
              z = 2;
              if (progress < segStart) {
                // fading in
                const t = (progress - (segStart - fadeBand)) / fadeBand;
                opacity = Math.max(0, t);
                y = 40 - 40 * t;
              } else if (progress > segEnd) {
                // fading out
                const t = (progress - segEnd) / fadeBand;
                opacity = Math.max(0, 1 - t);
                y = -20 * t;
              } else {
                opacity = 1;
                y = 0;
              }
            }
            // Last paragraph: keep visible past end
            if (idx === total - 1 && progress >= segEnd - fadeBand) {
              opacity = 1;
              y = 0;
              z = 2;
            }
            el.style.opacity = String(opacity);
            el.style.transform = `translateY(${y}px)`;
            el.style.zIndex = String(z);
          });
          // Progress bar
          if (progressBarRef.current) {
            progressBarRef.current.style.transform = `scaleX(${progress})`;
          }
          // Counter
          if (counterRef.current) {
            const idx = Math.min(total - 1, Math.floor(progress * total));
            counterRef.current.textContent = `${String(idx + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
          }
        },
      });

      // Runs when the media condition stops matching (resize / rotation) and
      // on unmount. gsap.matchMedia reverts the ScrollTrigger and the
      // gsap.set() values itself; the direct style writes from onUpdate are
      // ours to clear so the stacked layout starts clean.
      return () => {
        paragraphs.forEach((el) => {
          el.style.opacity = '';
          el.style.transform = '';
          el.style.zIndex = '';
        });
        if (progressBarRef.current) progressBarRef.current.style.transform = '';
        if (counterRef.current) {
          counterRef.current.textContent = `01 / ${String(total).padStart(2, '0')}`;
        }
        wrap.classList.remove('is-pinned');
      };
    });

    return () => mm.revert();
  }, []);

  return (
    <section ref={wrapRef} className="about-pinned">
      <div ref={stageRef} className="about-pinned-stage">
        <div className="about-pinned-head">
          <span className="eyebrow">About</span>
          <h2>
            Stories are not just told — <em>they are felt.</em>
          </h2>
        </div>
        <div className="about-pinned-grid">
          <div className="about-pinned-portrait-col">
            <div className="about-pinned-portrait">
              <ChromaCard src="/images/avatar.jpg" alt="Portrait of Zongheng Brian He" />
            </div>
            <div className="about-pinned-meta">
              <span>Studio</span>
              <EmailCopy />
              <span>Los Angeles, CA</span>
            </div>
          </div>
          <div className="about-pinned-text">
            {PARAGRAPHS.map((p, i) => (
              <div
                key={i}
                ref={(el) => {
                  paragraphsRef.current[i] = el;
                }}
                className="about-pinned-paragraph"
              >
                <span className="about-pinned-eyebrow">{p.eyebrow}</span>
                <div className="about-pinned-body">{p.body}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="about-pinned-meter" aria-hidden="true">
          <span ref={counterRef} className="about-pinned-counter">
            01 / 05
          </span>
          <div className="about-pinned-progress">
            <div ref={progressBarRef} className="about-pinned-progress-bar" />
          </div>
        </div>
      </div>
    </section>
  );
}
